import { useMemo, useState, useCallback } from 'react';
import { ZodError } from 'zod';
import { css, useTheme } from '@emotion/react';
import { DownloadSimple, Key, Trash } from '@phosphor-icons/react';
import type { ProfileDocument, SSHKeyDocument } from 'common/account/ProfileDocuments';
import { EditAppContext, useEditApp } from 'client/lib/EditApp';
import { useError } from 'client/lib/Error';
import { useUser } from 'client/lib/User';
import { ProfileState, useProfileState } from 'client/state/ProfileState';
import { AddSSHKeyDialog } from 'client/pages/profile/components/AddSSHKeyDialog';
import { Button } from 'client/components/Button';
import { Catalog, CatalogItemProps } from 'client/components/Catalog';
import { EditAppSaveButton } from 'client/components/EditApp/EditAppSaveButton';
import { Input } from 'client/components/Input';
import { Section } from 'client/components/Section/Section';
import { Stack } from 'client/components/Stack';
import { AnonymousTrialOnboarding } from './components/AnonymousTrialOnboarding';
import { deleteSSHKey } from './components/sshKeyHelpers';
import { SSHKeyInfo } from './components/SSHKeyInfo';

export const ProfileTab = () => {
  const theme = useTheme();
  const { user, updateSession } = useUser();
  const { setError } = useError();
  const [savedSSHKeys, setSavedSSHKeys] = useState<SSHKeyDocument[] | null>(null);
  const [isSSHKeyLoading, setIsSSHKeyLoading] = useState(false);
  const isAnonymousTrial = user && !!user.anonymousTrial;

  const editAppContext = useEditApp<ProfileDocument>({
    loadUrl: '/profile/load',
    saveUrl: '/profile/save',
    store: useProfileState,
    validator: (data: any) => new ZodError<any>([]),
  });
  const document = editAppContext.store((state) => state.document);

  const sshKeys: SSHKeyDocument[] = savedSSHKeys || document?.sshKeys || [];

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSSHKeyDelete = useCallback(
    async (keyID: number) => {
      setError(null);
      setIsSSHKeyLoading(true);
      const response = await deleteSSHKey({ keyID });
      setIsSSHKeyLoading(false);

      if (response.status !== 'ok') {
        return setError('Something went wrong while saving. Please try again…', { status: response.status });
      }

      setSavedSSHKeys(response.sshKeys);
      updateSession();
    },
    [setError, updateSession]
  );

  const sshKeyItems = useMemo<CatalogItemProps[]>(
    () =>
      sshKeys.map(({ keyID, fingerprint, description, timeCreate }) => ({
        id: `${keyID}`,
        title: description,
        subtitle: <SSHKeyInfo fingerprint={fingerprint} timeCreate={timeCreate} />,
        icon: <Key />,
        action: (
          <Button icon={<Trash color={theme.color.red[100]} />} disabled={isSSHKeyLoading} onClick={() => handleSSHKeyDelete(keyID)} />
        ),
      })) || [],
    [sshKeys, isSSHKeyLoading, handleSSHKeyDelete, theme]
  );

  const onChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    editAppContext.store.setState((state: ProfileState) => ({ document: { ...state.document!, [field]: event.target.value } }));
  };

  const handleSSHKeyAdd = (sshKeys: SSHKeyDocument[]) => {
    setSavedSSHKeys(sshKeys);
    setDialogOpen(false);
    updateSession();
  };

  if (isAnonymousTrial) return <AnonymousTrialOnboarding />;

  return (
    <EditAppContext.Provider value={editAppContext}>
      <Section title="Profile" subtitle="Basic information about you">
        <Stack>
          <div css={gridStyle}>
            <Input id="account-first-name" label="First Name" value={document?.firstName || ''} onChange={onChange('firstName')} />
            <Input id="account-last-name" label="Last Name" value={document?.lastName || ''} onChange={onChange('lastName')} />
          </div>

          <Input id="account-email" label="Email" value={document?.email || ''} onChange={onChange('email')} />
        </Stack>
      </Section>

      <Section title="Authentication" subtitle="Manage access to Diploi and its services">
        <Catalog title="SSH Keys" items={sshKeyItems} createButtonText="Add new SSH key" onCreate={() => setDialogOpen(true)} />
      </Section>

      <Section title="" css={actionsStyle}>
        <EditAppSaveButton icon={<DownloadSimple weight="regular" />} primary large>
          Save Changes
        </EditAppSaveButton>
      </Section>

      <AddSSHKeyDialog open={dialogOpen} onSave={handleSSHKeyAdd} onClose={() => setDialogOpen(false)} />
    </EditAppContext.Provider>
  );
};

const gridStyle = css`
  display: flex;
  flex-wrap: wrap;
  width: calc(100% + 1rem);

  > .input {
    margin-right: 1rem;
    width: calc(50% - 1rem);
  }
`;

const actionsStyle = css`
  border-bottom: none;
`;
